/**
 * ErrorState — a load failed, said in our words, with the one next step.
 *
 * Usually from an error:
 *
 *     <ErrorState error={query.error} resource="matters" onRetry={() => query.refetch()} />
 *
 * or explicitly:
 *
 *     <ErrorState variant="forbidden" title="Admins only" message="Ask an admin to change this." />
 *
 * Variants:
 *   generic   — message + Retry (when `onRetry` is passed and the failure is transient)
 *   plan      — 402. feature_not_in_plan → "Upgrade" to /billing/plans?feature=…;
 *               team_plan_required → "Needs a multi-seat plan"
 *   forbidden — 403
 *   notFound  — 404 / 410, with a link home
 *
 * The page stays where it is: a 402 is not a redirect. A query that renders
 * this for a 402 should set `meta: { suppressGlobalRedirect: true }` so the
 * global handler does not navigate away first.
 *
 * Never pass API text as `message` — see `errorStateProps`.
 */
import { AlertTriangle, Lock, RotateCw, SearchX, ShieldOff } from 'lucide-react';
import Link from 'next/link';
import type { ComponentType, ReactNode } from 'react';

import { Button, buttonClassName } from './Button';
import { cn } from './cn';
import type { ErrorStateVariant } from './errorStateProps';
import { errorStateProps } from './errorStateProps';

const ICONS: Record<
  ErrorStateVariant,
  ComponentType<{ size?: number; strokeWidth?: number; className?: string; 'aria-hidden'?: boolean }>
> = {
  generic: AlertTriangle,
  plan: Lock,
  forbidden: ShieldOff,
  notFound: SearchX,
};

const ICON_TONE: Record<ErrorStateVariant, string> = {
  generic: 'text-state-danger',
  plan: 'text-gold',
  forbidden: 'text-fg-muted',
  notFound: 'text-fg-muted',
};

export interface ErrorStateProps {
  /** The thrown error. Picks variant, title, message and link unless overridden. */
  error?: unknown;
  /** What failed, lower case ("matters", "this matter"), for the sentence. */
  resource?: string;
  variant?: ErrorStateVariant;
  title?: ReactNode;
  message?: ReactNode;
  /** Shown as a Retry button for generic failures. */
  onRetry?: () => void;
  /** Plan variant: override the upgrade link. */
  upgradeHref?: string;
  upgradeLabel?: string;
  /** notFound variant: where "Go to home" points. Defaults to /dashboard. */
  homeHref?: string;
  /** Extra or replacement actions. */
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function ErrorState({
  error,
  resource,
  variant,
  title,
  message,
  onRetry,
  upgradeHref,
  upgradeLabel,
  homeHref = '/dashboard',
  action,
  compact = false,
  className,
}: ErrorStateProps) {
  const mapped = error !== undefined ? errorStateProps(error, { resource }) : null;
  const v: ErrorStateVariant = variant ?? mapped?.variant ?? 'generic';
  const fallbackTitle =
    v === 'plan'
      ? 'Not included in your plan'
      : v === 'forbidden'
        ? 'You don’t have access'
        : v === 'notFound'
          ? 'We can’t find that'
          : resource
            ? `Couldn’t load ${resource}`
            : 'Couldn’t load this';
  const shownTitle = title ?? mapped?.title ?? fallbackTitle;
  const shownMessage = message ?? mapped?.message;
  const retryable = mapped ? mapped.retryable : v === 'generic';
  const upgrade = upgradeHref ?? mapped?.upgradeHref ?? (v === 'plan' ? '/billing/plans' : undefined);
  const Icon = ICONS[v];

  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-xl border border-slate-200 bg-white text-center',
        compact ? 'gap-1.5 px-3 py-4' : 'gap-2 px-4 py-8',
        className,
      )}
      data-error-state={v}
      role="alert"
    >
      <Icon aria-hidden className={ICON_TONE[v]} size={compact ? 18 : 22} strokeWidth={1.75} />
      <p className={cn('font-semibold text-fg', compact ? 'text-sm' : 'text-base')}>{shownTitle}</p>
      {shownMessage ? <p className="max-w-md text-sm text-fg-secondary">{shownMessage}</p> : null}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {v === 'plan' && upgrade ? (
          <Link className={buttonClassName({ size: compact ? 'sm' : 'md' })} href={upgrade}>
            {upgradeLabel ?? mapped?.upgradeLabel ?? 'Upgrade'}
          </Link>
        ) : null}
        {v === 'notFound' ? (
          <Link className={buttonClassName({ variant: 'secondary', size: compact ? 'sm' : 'md' })} href={homeHref}>
            Go to home
          </Link>
        ) : null}
        {onRetry && retryable ? (
          <Button leftIcon={RotateCw} size={compact ? 'sm' : 'md'} variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {action}
      </div>
    </div>
  );
}
